import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import Topbar from '../../Tools/Topbar';
import LeftSidebar from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import PatientNewCaseModal from './CaseDetails/PatientSituationModal';
import { Modal } from 'bootstrap';
import { format, parseISO } from 'date-fns';

// Component to fetch and store date and time formats in sessionStorage
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: {
            Authorization: 'Bearer 1349|VFSxfHmNSspz9ZhH1Nu9L5yxoD9vlzPoI9BKifiE324f1ef0',
          },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date and time formats:', err);
        // Fallback to defaults if API fails
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []); // Run once on mount

  return null; // No UI to render
};

// CaseDetailTab component for displaying detailed information about a specific EMS case
const CaseDetailTab = () => {
  // State declarations
  const [caseId, setCaseId] = useState(sessionStorage.getItem('currentCaseId'));
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);

  // Hooks and refs
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef(null);
  const patientModalRef = useRef(null);

  // Get date and time format from sessionStorage
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  // Utility function to format date based on settings
  const formatCaseDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
      const timeFormat = sessionStorage.getItem('time_format') || '24';

      // Map WordPress-style formats to date-fns syntax
      const dateFormatMap = {
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'YYYY-MM-DD': 'yyyy-MM-dd',
      };

      const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
      const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

      //  Parse both ISO and non-ISO timestamps as *UTC*, not local time
      let utcDate;
      if (dateString.includes('T')) {
        // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
        utcDate = new Date(dateString);
      } else {
        // MySQL-like (e.g. 2025-10-27 16:35:40)
        utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
      }

      // Format without applying local timezone
      const utcTimestamp = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );

      return format(utcTimestamp, formatString);
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return 'Invalid Date';
    }
  };

  // Toggle sidebar
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Handle file input click
  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('Selected file:', file.name);
    }
  };

  // Open patient situation modal
  const handleViewPatientSituation = () => {
    console.log('Opening PatientSituationModal for caseId:', caseId);
    sessionStorage.setItem('currentCaseId', caseId);
    setIsPatientModalOpen(true);
    if (patientModalRef.current) {
      console.log('Triggering modal show via patientModalRef');
      const modalElement = patientModalRef.current;
      const modal = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false,
      });
      modal.show();
    } else {
      console.warn('patientModalRef is not initialized');
    }
  };

  // Fetch case details
  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        setIsLoading(true);
        if (!caseId) {
          setError('No case selected');
          setIsLoading(false);
          return;
        }

        console.log('Fetching case details for caseId:', caseId);

        // UPDATED: Added proper headers as per your curl command
        const response = await axiosInstance.get(`/ems/cases/${caseId}`, {
          headers: {
            'Accept': 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            'Authorization': 'Bearer 1349|VFSxfHmNSspz9ZhH1Nu9L5yxoD9vlzPoI9BKifiE324f1ef0'
          }
        });

        console.log('API Response:', response.data);

        // UPDATED: Handle both array and object responses
        let caseDataResponse;
        if (Array.isArray(response.data)) {
          // If response is an array, find the case with matching ID
          caseDataResponse = response.data.find(caseItem => caseItem.id === parseInt(caseId));
          if (!caseDataResponse && response.data.length > 0) {
            // If no exact match, use first item (fallback)
            caseDataResponse = response.data[0];
          }
        } else if (response.data.data) {
          // If response has data property
          caseDataResponse = response.data.data;
        } else {
          // Direct object response
          caseDataResponse = response.data;
        }

        if (caseDataResponse) {
          setCaseData(caseDataResponse);
          console.log('Patient Location Data:', {
            p_location: caseDataResponse.p_location,
            p_area: caseDataResponse.p_area
          });
        } else {
          setCaseData(null);
          setError('Case not found in response');
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching case details:', err);
        setError(err.response?.data?.message || 'Failed to fetch case details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaseDetails();

    return () => {
      console.log('Cleaning up: Removing currentCaseId from sessionStorage');
      // sessionStorage.removeItem('currentCaseId');
    };
  }, [caseId]);

  // Initialize Bootstrap modal for patient situation
  useEffect(() => {
    const modalElement = document.getElementById('bs-fullscreen');
    if (modalElement && isPatientModalOpen) {
      console.log('Initializing PatientSituationModal with ID bs-fullscreen');
      const modal = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false,
      });
      modal.show();

      const handleModalClose = () => {
        console.log('PatientSituationModal closed');
        setIsPatientModalOpen(false);
      };
      modalElement.addEventListener('hidden.bs.modal', handleModalClose);

      return () => {
        console.log('Cleaning up PatientSituationModal event listener');
        modalElement.removeEventListener('hidden.bs.modal', handleModalClose);
      };
    } else if (isPatientModalOpen) {
      console.warn('Modal element with ID bs-fullscreen not found in DOM');
    }
  }, [isPatientModalOpen]);

  // Extract documents
  const getDocuments = () => {
    if (!caseData) return [];

    const documents = [];
    if (caseData.documents1) documents.push(caseData.documents1);
    if (caseData.documents2) documents.push(caseData.documents2);
    if (caseData.documents3) documents.push(caseData.documents3);

    return documents.filter((doc) => doc && doc.trim() !== '');
  };

  // Handle document click
  const handleDocumentClick = (docUrl) => {
    console.log('Opening document modal for URL:', docUrl);
    setSelectedDocument(docUrl);
    setShowDocumentModal(true);
  };

  // Render document content
  const renderDocumentContent = (url) => {
    const extension = url.split('.').pop().toLowerCase();

    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return <img src={url} alt="attachment" className="w-100" style={{ maxHeight: '500px', objectFit: 'contain' }} />;
    } else if (extension === 'pdf') {
      return <iframe src={url} width="100%" height="500px" title="PDF Document" />;
    } else if (extension === 'xml') {
      return <iframe src={url} width="100%" height="500px" title="XML Document" />;
    } else {
      return <p>Unsupported file type: {extension}</p>;
    }
  };

  // Render error state
  if (error) {
    return (
      <div className="alert alert-danger m-4">
        {error}
      </div>
    );
  }

  // Render main component
  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Case Detail</h5>
                <p className="card-subtitle mb-0 mt-1">Comprehensive case overview including patient, EMS, and medical facility details</p>
              </div>
              <div className="card-body p-4">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {caseData ? (
                      <div className="py-3 px-0">
                        <div className="table-responsive rounded-2 ros-table">
                          <table className="table text-nowrap customize-table mb-0 align-middle">
                            <thead className="text-dark fs-4">
                              <tr>
                                <th><h6 className="fw-semibold mb-0">Field</h6></th>
                                <th><h6 className="fw-semibold mb-0">Value</h6></th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                { label: 'Case ID', value: caseData.id },
                                { label: 'Name', value: caseData.case_name },
                                { label: 'Location', value: caseData.location?.location_name || 'N/A' },
                                { label: 'Ambulance', value: caseData.ambulance?.plate_number || 'N/A' },
                                { label: 'Medical Center', value: caseData.medical_center?.name || 'N/A' },
                                { label: 'Paramedics ID', value: caseData.ems_member || 'N/A' },
                                { label: 'Paramedics Name', value: caseData.ems_member_name || 'N/A' },
                                { label: 'Paramedics Status', value: caseData.ems_member?.status ? 'Busy' : 'Available' },
                                // UPDATED: Patient Location and Area - data is properly available in your API response
                                { label: 'Patient Location', value: caseData.p_location?.location_name || 'N/A' },
                                { label: 'Patient Area', value: caseData.p_area?.area_name || 'N/A' },
                                { label: 'Accident Place', value: caseData.accident_place || 'N/A' },
                                { label: 'Gender', value: caseData.gender || 'N/A' },
                                { label: 'Age', value: caseData.age || 'N/A' },
                                { label: 'Bracelet ID', value: caseData.bracelet_id || 'N/A' },
                                { label: 'Type of Injury', value: caseData.injury_type || 'N/A' },
                                { label: 'Contact Number', value: caseData.contact_number || 'N/A' },
                                { label: 'Triage Card', value: caseData.triage_card || 'N/A' },
                                { label: 'Acknowledgment', value: caseData.acknowledgment || 'Pending' },
                                {
                                  label: 'Documents',
                                  value: (
                                    <p className="mb-0 fw-normal">
                                      <i className="ti ti-paperclip me-2"></i>
                                      {getDocuments().length > 0 ? (
                                        getDocuments().map((doc, index) => (
                                          <button
                                            key={index}
                                            type="button"
                                            className="btn btn-link me-3 p-0"
                                            onClick={() => handleDocumentClick(doc)}
                                            title={`Document ${index + 1}`}
                                          >
                                            <i className="ti ti-library-photo"></i>
                                          </button>
                                        ))
                                      ) : (
                                        'No documents'
                                      )}
                                    </p>
                                  ),
                                },
                                { label: 'Case Date', value: formatCaseDate(caseData.created_at) },
                                {
                                  label: 'Patient Situation',
                                  value: (
                                    <p className="mb-0 fw-normal">
                                      <button
                                        className="text-primary bg-transparent border-0 p-0"
                                        onClick={handleViewPatientSituation}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        Click to view details
                                      </button>
                                    </p>
                                  ),
                                },
                              ].map(({ label, value }, index) => (
                                <tr key={index}>
                                  <td><p className="mb-0 fw-normal fw-semibold">{label}</p></td>
                                  <td>{value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p>No case data found</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div
              className={`modal fade ${showDocumentModal ? 'show d-block' : ''}`}
              id="document-modal"
              tabIndex="-1"
              aria-labelledby="documentModalLabel"
              aria-modal="true"
              role="dialog"
            >
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header d-flex align-items-center">
                    <h4 className="modal-title" id="documentModalLabel">Attachment</h4>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowDocumentModal(false)}
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body">{selectedDocument && renderDocumentContent(selectedDocument)}</div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn bg-danger-subtle text-danger font-medium"
                      onClick={() => setShowDocumentModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <PatientNewCaseModal
              ref={patientModalRef}
              isOpen={isPatientModalOpen}
              onClose={() => {
                console.log('Closing PatientSituationModal via onClose');
                setIsPatientModalOpen(false);
                if (patientModalRef.current) {
                  patientModalRef.current.hide();
                }
              }}
              caseId={caseId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseDetailTab;